import {
	AssetManager,
	GameAssets,
	ParamDescriptor,
	ParamManager,
	SpineDescriptor,
	TunerService,
	inject,
	service,
} from "@spawn/core";
import { GoldenBonusAssetsService, GoldenBonusOverlayView, GoldenBonusReelView } from "@spawn/golden-bonus";
import { Presets as ModernUiPresets } from "@spawn/modern-ui";
import { NudgingOverlayView } from "@spawn/nudge";
import { RevealOverlayView } from "@spawn/reveal";
import { AmbientSoundController, NetworkService, ScatterModule, SlotMachinePresets, WinSoundService } from "@spawn/slot-machine";
import { FreegameIntroState, FreegameOutroView, NudgeUpsCounterView, ReelSetContainerView } from "@spawn/nrg-games-core";
import { SymbolService } from "./services/symbolService";
import { FreegameIntroView } from "./views/freegames/freegameIntroView";
import { MultiplerNotchView } from "./views/freegames/multiplierNotchView";
import { MariachiWinSoundView } from "./views/mariachiWinSoundView";
import { ReelSetFrameView } from "./views/reelSetFrameView";

@service
export class Assets extends GameAssets {
	@inject private readonly params!: ParamManager;
	@inject protected readonly networkService!: NetworkService;

	private gameSpineDescriptor!: SpineDescriptor;

	protected presets = [
		SlotMachinePresets.coinShower(this.networkService.casinoOptions.branding),
		SlotMachinePresets.gambling(this.networkService.casinoOptions.branding),
		SlotMachinePresets.winParty(),
		[...SlotMachinePresets.reelStopSounds(), ...SlotMachinePresets.reelQuickStopSound()],
		[
			AssetManager.createDescriptor(WinSoundService, "backingSound", "@spawn/core-assets:std_wc_backing.mp4"),
			AssetManager.createDescriptor(WinSoundService, "teaserBellSound", "@spawn/core-assets:std_wc_largewintease_2s.mp4"),
			AssetManager.createDescriptor(WinSoundService, "stepSoundNice", "@spawn/core-assets:std_wc_step_nice.mp4"),
			AssetManager.createDescriptor(WinSoundService, "stepSoundBig", "@spawn/core-assets:std_wc_step_big.mp4"),
			AssetManager.createDescriptor(WinSoundService, "stepSoundMega", "@spawn/core-assets:std_wc_step_mega.mp4"),
			AssetManager.createDescriptor(WinSoundService, "stepSoundMonster", "@spawn/core-assets:std_wc_step_monster.mp4"),
			AssetManager.createDescriptor(WinSoundService, "stepSoundLegendary", "@spawn/core-assets:std_wc_step_legendary.mp4"),

			AssetManager.createDescriptor(WinSoundService, "finishSoundSmall0", "fdm_win_0.mp4"),
			AssetManager.createDescriptor(WinSoundService, "finishSoundSmall1", "fdm_win_1.mp4"),
			AssetManager.createDescriptor(WinSoundService, "finishSoundSmall2", "fdm_win_2.mp4"),
			AssetManager.createDescriptor(WinSoundService, "finishSoundSmall3", "fdm_win_3.mp4"),
			AssetManager.createDescriptor(WinSoundService, "finishSoundNice", "@spawn/core-assets:std_wc_finish_nice.mp4"),
			AssetManager.createDescriptor(WinSoundService, "finishSoundBig", "@spawn/core-assets:std_wc_finish_big.mp4"),
			AssetManager.createDescriptor(WinSoundService, "finishSoundMega", "@spawn/core-assets:std_wc_finish_mega.mp4"),
			AssetManager.createDescriptor(WinSoundService, "finishSoundMonster", "@spawn/core-assets:std_wc_finish_monster.mp4"),
			AssetManager.createDescriptor(WinSoundService, "finishSoundLegendary", "@spawn/core-assets:std_wc_finish_legendary.mp4"),

			...AssetManager.createMultipleDescriptors(
				WinSoundService,
				"linePingSounds",
				["fdm_lineping_01.mp4"],
				["fdm_lineping_02.mp4"],
				["fdm_lineping_03.mp4"],
				["fdm_lineping_04.mp4"],
				["fdm_lineping_05.mp4"],
				["fdm_lineping_06.mp4"],
				["fdm_lineping_07.mp4"],
				["fdm_lineping_08.mp4"],
				["fdm_lineping_09.mp4"],
				["fdm_lineping_10.mp4"],
				["fdm_lineping_11.mp4"],
				["fdm_lineping_12.mp4"]
			),
		],
		ModernUiPresets.icons(this.networkService.casinoOptions.branding),
		ModernUiPresets.sounds(),
	];

	constructor() {
		super();

		const soundParams = {
			// Game specific
			"ReelSoundAssets.attentionSpinSounds.fadeOutTime": 0.8,
			"ReelSetContainerView.attentionSpinSound.fadeOutTime": 0.8,
			"AmbientSoundController.sounds.volume": 0.4,
			"AmbientSoundController.sounds.fadeInTime": 0.5,
			"AmbientSoundController.sounds.fadeOutTime": 0.5,
			"CountingModule.ambientMusicReducedVolume": 0.4,
			"WinSoundService.linePingSounds.fadeOutTime": 1,
			// // Win celebration fix:
			"WinSoundService.backingSound.fadeOutTime": 0.25,
			"WinSoundService.teaserBellSound.fadeOutTime": 1,
			"WinSoundService.stepSoundNice.fadeOutTime": 1,
			"WinSoundService.stepSoundBig.fadeOutTime": 1,
			"WinSoundService.stepSoundMega.fadeOutTime": 1,
			"WinSoundService.stepSoundMonster.fadeOutTime": 1,
			"WinSoundService.stepSoundLegendary.fadeOutTime": 1,
			"WinSoundService.finishSoundSmall0.fadeOutTime": 1,
			"WinSoundService.finishSoundSmall1.fadeOutTime": 1,
			"WinSoundService.finishSoundSmall2.fadeOutTime": 1,
			"WinSoundService.finishSoundSmall3.fadeOutTime": 1,
			"WinSoundService.finishSoundNice.startOffset": 0.05,
			"WinSoundService.finishSoundNice.fadeOutTime": 1,
			"WinSoundService.finishSoundBig.startOffset": 0.05,
			"WinSoundService.finishSoundBig.fadeOutTime": 1,
			"WinSoundService.finishSoundMega.startOffset": 0.05,
			"WinSoundService.finishSoundMega.fadeOutTime": 1,
			"WinSoundService.finishSoundMonster.startOffset": 0.05,
			"WinSoundService.finishSoundMonster.fadeOutTime": 1,
			"WinSoundService.finishSoundLegendary.startOffset": 0.05,
			"WinSoundService.finishSoundLegendary.fadeOutTime": 1,
			// second batch of volume changes
			"ReelSoundAssets.soundsReelStop.volume": 0.2,
			"ReelSoundAssets.quickStopSound.volume": 0.2,
			"ModernUiSounds.soundSpinClick.volume": 0.2,
			"WinSoundService.linePingSounds.volume": 0.75,
			"SymbolService.scatterTeaserSounds.volume": 0.6,
			"ReelSoundAssets.attentionSpinSounds.volume": 0.6,
			"ReelSetContainerView.attentionSpinSound.volume": 0.6,
			"ScatterModule.scatterAnimationSound.fadeOutTime": 0.5,
			"MariachiWinSoundView.mariachiSoundResource.fadeOutTime": 1,
			"NudgingOverlayView.revealNudgeSound.volume": 0.4,
			"RevealOverlayView.coverSound.volume": 0.6,
			"RevealOverlayView.uncoverSound.volume": 0.6,
			"GoldenBonusOverlayView.triggerSound.volume": 0.7,
		};

		for (const [name, value] of Object.entries(soundParams)) {
			const paramHandle = TunerService.getParamHandleByName(name)!;
			this.params.updateParams(new ParamDescriptor(paramHandle.id, value));
		}

		this.bindDescriptors(ReelSetContainerView, ({ create }) => {
			create("attentionSpinSound", "fdm_attentionspin.mp4");
			create("teaserSound", "fdm_mystery_teaser.mp4");
			this.gameSpineDescriptor = create("attentionSpinAnimation", "attentionspin.json");
		});

		this.bindDescriptors(SymbolService, ({ create, createMultiple }) => {
			create("lvAnimation", "lv.json", this.gameSpineDescriptor);
			create("hv1Animation", "hv1.json", this.gameSpineDescriptor);
			create("hv2Animation", "hv2.json", this.gameSpineDescriptor);
			create("hv3Animation", "hv3.json", this.gameSpineDescriptor);
			create("hv4Animation", "hv4.json", this.gameSpineDescriptor);

			create("scatterAnimation", "scatter.json", this.gameSpineDescriptor);
			create("mysteryAnimation", "mystery.json", this.gameSpineDescriptor);

			create("symbolLV1", "lv1.png");
			create("symbolLV2", "lv2.png");
			create("symbolLV3", "lv3.png");
			create("symbolLV4", "lv4.png");
			create("symbolHV1", "hv1.png");
			create("symbolHV2", "hv2.png");
			create("symbolHV3", "hv3.png");
			create("symbolHV4", "hv4.png");
			create("symbolTopFull", "top_wild.png");
			create("symbolTopCut", "top_mystery_2.png");
			create("symbolScatter", "scatter_bg.png");
			create("symbolScatterFG", "scatter_fg.png");
			create("symbolMystery", "mystery.png");
			create("symbolMysteryFrame", "mystery_frame.png");
			create("symbolTopGold", "top_reveal_1.png");
			create("symbolTopGoldBig", "top_reveal_2.png");

			createMultiple(
				"scatterTeaserSounds",
				["fdm_scatter_teaser_1.mp4"],
				["fdm_scatter_teaser_2.mp4"],
				["fdm_scatter_teaser_3.mp4"],
				["fdm_scatter_teaser_4.mp4"],
				["fdm_scatter_teaser_5.mp4"]
			);
		});

		this.bindDescriptors(GoldenBonusReelView, ({ create, createMultiple }) => {
			create("frameTexture", "mystery_frame.png");
			createMultiple("lowBonusStopSounds", ["fdm_calaveras_teaser_1.mp4"]);
			createMultiple(
				"bonusStopSounds",
				["fdm_calaveras_teaser_2.mp4"],
				["fdm_calaveras_teaser_3.mp4"],
				["fdm_calaveras_teaser_4.mp4"]
			);
		});

		this.bindDescriptors(GoldenBonusOverlayView, ({ create }) => {
			create("triggerSound", "fdm_mystery_reveal_golden.mp4");
		});

		this.bindDescriptors(GoldenBonusAssetsService, ({ create }) => {
			create("symbolBonus1", "bonus_1.png");
			create("symbolBonus2", "bonus_2.png");
			create("symbolBonus5", "bonus_5.png");
			create("symbolBonus10", "bonus_10.png");
			create("symbolBonus25", "bonus_25.png");
			create("symbolBonus50", "bonus_50.png");
			create("symbolBonus100", "bonus_100.png");
			create("symbolBonus250", "bonus_250.png");
			create("symbolBonus500", "bonus_500.png");
			create("symbolBonus1000", "bonus_1000.png");
			create("symbolBonus2500", "bonus_2500.png");
			create("bonusSymbolSpine", "bonus.json", this.gameSpineDescriptor);
			create("goldenTriggerSpine", "top_golden-bonus.json", this.gameSpineDescriptor);
		});

		this.bindDescriptors(RevealOverlayView, ({ create }) => {
			create("uncoverSound", "fdm_mystery_reveal_normal.mp4");
			create("coverSound", "fdm_mystery_flowers_in.mp4");
		});

		this.bindDescriptors(NudgingOverlayView, ({ create }) => {
			create("revealNudgeSound", "fdm_mystery_nudge.mp4");
		});

		this.bindDescriptors(ReelSetFrameView, ({ create }) => {
			create("bgFrameTexture", "reels_bg.png");
			create("bgLogoTexture", "reels_titlehutch_bg.png");
			create("fgFrameTexture", "reels_fg.png");
			create("fgLogoTexture", "reels_titlehutch_fg.png");
		});

		// Freegames
		this.bindDescriptors(FreegameIntroView, ({ create }) => {
			create("fgReelsTexture", "info_fg_reels.png");
			create("fgMysteryTexture", "intro_fg_mystery.png");
		});
		this.bindDescriptors(FreegameOutroView, ({ create }) => {
			create("freegamesOutroFrame", "outro.png");
			create("outroSound", "fdm_fg_outro.mp4");
		});
		this.bindDescriptors(ScatterModule, ({ create }) => {
			create("scatterAnimationSound", "fdm_scatter_trigger.mp4");
		});
		this.bindDescriptors(FreegameIntroState, ({ create }) => {
			create("freegameIntroSound", "fdm_fanfare.mp4");
		});
		this.bindDescriptors(MultiplerNotchView, ({ create }) => {
			create("notchTexture", "reels_top_multiplier_fg.png");
			create("multiplierSpine", "multiplier_bar.json", this.gameSpineDescriptor);
		});
		this.bindDescriptors(NudgeUpsCounterView, ({ create }) => {
			create("nudgeCounterSpine", "nudging_counter.json", this.gameSpineDescriptor);
		});

		this.bindDescriptors(AmbientSoundController, ({ createMultiple }) => {
			createMultiple(
				"sounds",
				["@spawn/core-assets:std_takewinorriskloop.mp4"],
				["fdm_bg_background_loop.mp4"],
				["fdm_bg_background_loop.mp4"],
				["fdm_fg_background_loop.mp4"]
			);
		});

		this.bindDescriptors(MariachiWinSoundView, ({ create }) => {
			create("mariachiSoundResource", "fdm_win_mariachis_overlay.mp4");
		});
	}
}
